import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { useCapabilitiesQuery } from "modules/capabilities/hooks/useCapabilitiesQuery"
import { useTranslation } from "react-i18next"
import { WorkflowNodeSchema, CapabilityIdentifier } from "schema"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"

type Props = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

export function TriggerCapabilityConfiguration({ nodeIndex, node }: Props) {
  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const { data: capabilities } = useCapabilitiesQuery()

  const workflowCapabilities = capabilities?.filter((c) => c.isAvailableInWorkflows) ?? []

  const selectedIdentifier =
    node.config && "capabilityIdentifier" in node.config
      ? (node.config.capabilityIdentifier as string)
      : undefined

  function handleSelect(value: string) {
    form.setValue(
      `nodes.${nodeIndex}.config`,
      { capabilityIdentifier: value },
      { shouldDirty: true },
    )
    form.trigger(`nodes.${nodeIndex}.config`)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label>{t("campaigns.action.availableActions.TriggerCapability.config.capability")}</Label>
        <Select value={selectedIdentifier} onValueChange={handleSelect}>
          <SelectTrigger>
            <SelectValue
              placeholder={t(
                "campaigns.action.availableActions.TriggerCapability.config.placeholder",
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {workflowCapabilities.map((capability) => (
              <SelectItem key={capability.identifier} value={capability.identifier}>
                {t(`capabilities.catalog.${capability.identifier}.name`, {
                  defaultValue: capability.name ?? capability.identifier,
                })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {selectedIdentifier === CapabilityIdentifier.RequestReview && (
        <p className="text-sm text-muted-foreground">
          {t("campaigns.action.availableActions.TriggerCapability.config.requestReviewDescription")}
        </p>
      )}
    </div>
  )
}
